import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Empty, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import { containerUserApi as api, isOk, errMsg } from '../../../shared/services/containerService';
import { ContainerStatusTag, PowerButtons, specText, needsPoll, arr } from '../../../shared/components/container/ContainerParts';

const { Text } = Typography;

const MyContainers = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        const r = await api.list();
        setLoading(false);
        // Paid orders still waiting for a node (开通中) come first; they have no container yet.
        // 旧版（应用容器）未迁移前也在同一列表，标记「旧版」。
        if (isOk(r)) {
            setList([...arr(r.data?.pending).map((p) => ({ ...p, key: `p${p.subscription_id}` })), ...arr(r.data?.list),
                ...arr(r.data?.legacy).map((l) => ({ ...l, key: `l${l.id}`, legacy: true }))]);
        }
        else if (!silent) message.error(errMsg(r, t('Failed to load')));
    }, [t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        clearTimeout(timer.current);
        if (list.some((x) => !x.legacy && needsPoll(x))) timer.current = setTimeout(() => load(true), 10000);
        return () => clearTimeout(timer.current);
    }, [list, load]);

    const goCreate = () => navigate(userPath('/containers/create'));

    const columns = [
        {
            title: t('Name'), key: 'name', render: (_, c) => (
                <Space direction="vertical" size={2}>
                    <Space size={8} wrap>
                        <Text strong>{c.label || c.name || `#${c.id}`}</Text>
                        <ContainerStatusTag status={c.status} busy={c.busy} />
                        {c.legacy && <Tag>{t('Legacy')}</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>{c.image}</Text>
                    {c.status === 'error' && c.error_msg && <Text type="danger" style={{ fontSize: 12 }}>{c.error_msg}</Text>}
                </Space>
            ),
        },
        { title: t('Configuration'), key: 'spec', render: (_, c) => specText(c) },
        { title: t('Private IP'), dataIndex: 'private_ip', render: (v) => (v ? <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text> : '-') },
        { title: t('Expires'), dataIndex: 'expires_at', render: (v) => (v ? String(v).slice(0, 10) : '-') },
        {
            title: t('Actions'), key: 'op', width: 170, render: (_, c) => (c.legacy ? null : c.status === 'provisioning'
                ? <Tooltip title={t('Starts automatically once capacity is available; your paid term begins at activation')}><Text type="secondary">{t('Queued')}</Text></Tooltip>
                : <PowerButtons c={c} api={api} onDone={() => setTimeout(() => load(true), 1500)} onDeleted={() => load(true)} />),
        },
    ];

    return (
        <div>
            <PageHeader
                title={t('Dedicated Containers')}
                extra={<>
                    <Button icon={<SyncOutlined />} onClick={() => load()} loading={loading} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={goCreate}>{t('Create')}</Button>
                </>}
            />
            <Card styles={{ body: { padding: 0 } }}>
                <Table
                    rowKey={(r) => r.key || r.id}
                    columns={columns}
                    dataSource={list}
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    onRow={(r) => (r.id ? rowLink(navigate, (x) => (x.legacy ? `/containers/legacy/${x.id}` : `/containers/${x.id}`))(r) : {})}
                    rowClassName={(r) => (r.id ? ROW_LINK_CLASS : '')}
                    pagination={list.length > 20 ? { pageSize: 20 } : false}
                    locale={{
                        emptyText: (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No containers')}>
                                <Button type="primary" icon={<PlusOutlined />} onClick={goCreate}>{t('Create')}</Button>
                            </Empty>
                        ),
                    }}
                />
            </Card>
        </div>
    );
};

export default MyContainers;
